import { useEffect, useState } from 'react';

export default function HomePage() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://public.readdy.ai/ai/img_res/05721777-0240-4242-a29c-bd4714b7ea2b.png" alt="ZOLUCIONA LTDA Logo" className="h-10 w-10 object-contain" />
              <h1 className="text-xl font-bold text-gray-900">ZOLUCIONA LTDA</h1>
            </div>
            <div className="flex items-center gap-8">
              <button onClick={() => scrollToSection('products')} className="text-sm font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Products</button>
              <button onClick={() => scrollToSection('about')} className="text-sm font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">About</button>
              <button onClick={() => scrollToSection('contact')} className="text-sm font-light text-gray-700 hover:text-gray-900 transition-colors cursor-pointer whitespace-nowrap">Contact</button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center pt-20">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        <div className="relative max-w-7xl mx-auto px-6 py-20 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            <div className="lg:col-span-3">
              <h2 className="text-6xl lg:text-7xl font-bold text-gray-900 leading-tight mb-6">
                Modern iOS<br />
                <span className="font-extrabold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">Applications</span>
              </h2>
              <p className="text-xl text-gray-600 font-light mb-8 max-w-xl">
                VPN & Cleaner Solutions for Enhanced Mobile Security
              </p>
              <div className="flex flex-wrap gap-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center">
                    <i className="ri-shield-check-line text-xl text-indigo-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Secure</p>
                    <p className="text-xs text-gray-600">Enterprise-grade protection</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <i className="ri-speed-line text-xl text-purple-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Fast</p>
                    <p className="text-xs text-gray-600">Optimized performance</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center">
                    <i className="ri-user-smile-line text-xl text-pink-600"></i>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Simple</p>
                    <p className="text-xs text-gray-600">Intuitive interface</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="lg:col-span-2 relative">
              <div className="relative w-full h-96 lg:h-[500px]">
                <img 
                  src="https://public.readdy.ai/ai/img_res/6b3726ffb79e8b0f203d91bf1b205f50.jpg" 
                  alt="iOS Applications" 
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-5xl font-bold text-gray-900 mb-4">Our Products</h3>
            <div className="w-24 h-1 bg-gradient-to-r from-indigo-600 to-purple-600 mx-auto rounded-full"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* VPN Card */}
            <div className="relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-indigo-600 via-purple-600 to-indigo-800"></div>
              <div className="relative p-10 h-full flex flex-col">
                <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6">
                  <i className="ri-shield-keyhole-line text-4xl text-white"></i>
                </div>
                <h4 className="text-3xl font-bold text-white mb-4">VPN</h4>
                <p className="text-white/90 mb-6 text-sm leading-relaxed">
                  Secure your online presence with our advanced VPN solution designed specifically for iOS devices.
                </p>
                <ul className="space-y-3 flex-grow">
                  <li className="flex items-start gap-3 text-white/90 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></span>
                    <span>Military-grade encryption for complete privacy</span>
                  </li>
                  <li className="flex items-start gap-3 text-white/90 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></span>
                    <span>Global server network for optimal speed</span>
                  </li>
                  <li className="flex items-start gap-3 text-white/90 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></span>
                    <span>One-tap connection with automatic protection</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Cleaner Card */}
            <div className="relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 cursor-pointer">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-600"></div>
              <div className="relative p-10 h-full flex flex-col">
                <div className="w-20 h-20 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center mb-6">
                  <i className="ri-delete-bin-line text-4xl text-white"></i>
                </div>
                <h4 className="text-3xl font-bold text-white mb-4">Cleaner</h4>
                <p className="text-white/90 mb-6 text-sm leading-relaxed">
                  Optimize your device performance with intelligent cleaning and storage management tools.
                </p>
                <ul className="space-y-3 flex-grow">
                  <li className="flex items-start gap-3 text-white/90 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></span>
                    <span>Smart storage analysis and optimization</span>
                  </li>
                  <li className="flex items-start gap-3 text-white/90 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></span>
                    <span>Remove duplicate photos and large files</span>
                  </li>
                  <li className="flex items-start gap-3 text-white/90 text-sm">
                    <span className="w-1.5 h-1.5 rounded-full bg-white/80 mt-2 flex-shrink-0"></span>
                    <span>Boost device speed and battery life</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border border-gray-100">
              <div className="w-16 h-16 rounded-xl bg-indigo-100 flex items-center justify-center mb-6">
                <i className="ri-shield-check-line text-3xl text-indigo-600"></i>
              </div>
              <h5 className="text-xl font-bold text-gray-900 mb-3">Enterprise Security</h5>
              <p className="text-sm text-gray-600 leading-relaxed">
                Bank-level encryption and security protocols to protect your data and privacy at all times.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border border-gray-100">
              <div className="w-16 h-16 rounded-xl bg-purple-100 flex items-center justify-center mb-6">
                <i className="ri-speed-up-line text-3xl text-purple-600"></i>
              </div>
              <h5 className="text-xl font-bold text-gray-900 mb-3">Lightning Fast</h5>
              <p className="text-sm text-gray-600 leading-relaxed">
                Optimized algorithms ensure maximum performance without compromising your device battery life.
              </p>
            </div>

            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 cursor-pointer border border-gray-100">
              <div className="w-16 h-16 rounded-xl bg-emerald-100 flex items-center justify-center mb-6">
                <i className="ri-smartphone-line text-3xl text-emerald-600"></i>
              </div>
              <h5 className="text-xl font-bold text-gray-900 mb-3">User-Friendly</h5>
              <p className="text-sm text-gray-600 leading-relaxed">
                Intuitive interface designed for seamless user experience with minimal learning curve required.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-2 bg-gray-100 rounded-full mb-6">
                <span className="text-xs font-semibold text-gray-600 uppercase tracking-wider">Who We Are</span>
              </div>
              <h3 className="text-5xl font-bold text-gray-900 mb-4">
                A Team of Experts
              </h3>
              <p className="text-3xl font-serif italic text-gray-700 mb-6">
                Developing Modern Applications
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                ZOLUCIONA LTDA is a dedicated team of iOS development experts committed to creating innovative mobile applications that enhance user experience and improve device security. Our flagship products, VPN and Cleaner, represent years of expertise in mobile development and cybersecurity.
              </p>
              <p className="text-base text-gray-600 leading-relaxed mb-6">
                We believe in combining cutting-edge technology with intuitive design to deliver applications that not only meet but exceed user expectations. Our focus on quality, security, and performance has made us a trusted name in iOS application development.
              </p>
              <p className="text-base text-gray-600 leading-relaxed">
                Every application we develop undergoes rigorous testing and optimization to ensure it provides maximum value to our users while maintaining the highest standards of security and privacy protection.
              </p>
            </div>
            <div className="relative">
              <div className="relative w-full h-[500px] rounded-3xl overflow-hidden">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20iOS%20development%20team%20working%20together%20in%20modern%20office%20with%20MacBooks%20and%20iPhones%20collaborative%20workspace%20bright%20natural%20lighting%20clean%20minimalist%20environment%20diverse%20team%20members%20focused%20on%20coding%20and%20design%20contemporary%20tech%20company%20atmosphere&width=600&height=700&seq=about001&orientation=portrait" 
                  alt="ZOLUCIONA LTDA Team" 
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="absolute -bottom-6 -right-6 w-48 h-48 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-3xl opacity-20 blur-3xl"></div>
              <div className="absolute -top-6 -left-6 w-48 h-48 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-3xl opacity-20 blur-3xl"></div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Footer */}
      <section id="contact" className="relative py-20 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-t-[40px]">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute bottom-0 left-0 text-[180px] font-bold text-white">ZOLUCIONA</div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
            {/* Company Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <img src="https://public.readdy.ai/ai/img_res/05721777-0240-4242-a29c-bd4714b7ea2b.png" alt="ZOLUCIONA LTDA Logo" className="h-10 w-10 object-contain" />
                <h4 className="text-2xl font-bold text-white">ZOLUCIONA LTDA</h4>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                iOS Development Excellence
              </p>
            </div>

            {/* Address */}
            <div>
              <h5 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                <i className="ri-map-pin-line text-indigo-400"></i>
                Location
              </h5>
              <div className="space-y-2 text-sm text-gray-400">
                <p>CARRERA 14 23 15</p>
                <p>NEIVA, 410001</p>
                <p>Colombia</p>
              </div>
            </div>

            {/* Contact Methods */}
            <div>
              <h5 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
                <i className="ri-mail-line text-indigo-400"></i>
                Get in Touch
              </h5>
              <div className="space-y-3">
                <div className="flex items-start gap-2">
                  <i className="ri-mail-line text-indigo-400 mt-1"></i>
                  <a href="mailto:info@zoluciona-ltda.com" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer break-all">
                    info@zoluciona-ltda.com
                  </a>
                </div>
                <div className="flex items-start gap-2">
                  <i className="ri-phone-line text-indigo-400 mt-1"></i>
                  <a href="tel:+15709384872" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                    +1 5709384872
                  </a>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h5 className="text-base font-semibold text-white mb-4">Quick Links</h5>
              <div className="space-y-3">
                <button onClick={() => scrollToSection('home')} className="block text-sm text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Home</button>
                <button onClick={() => scrollToSection('products')} className="block text-sm text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Products</button>
                <button onClick={() => scrollToSection('about')} className="block text-sm text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">About</button>
                <button onClick={() => scrollToSection('contact')} className="block text-sm text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">Contact</button>
              </div>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-700">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4">
              <p className="text-sm text-gray-400">
                © 2025 ZOLUCIONA LTDA. All rights reserved.
              </p>
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-sm text-gray-400 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                Powered by Readdy
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
